import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import UserModel from "app/models/UserModel";
import { StripePurchaseModel } from "app/models/scheduling/StripePurchaseModel";
import { schedulingFetchGet, schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import CountdownTimer from "app/components/CountdownTimer";
import { toast } from "react-toastify";

import ButtonVariant from "app/components/Button/ButtonVariant";
import CancelPurchaseConfirmationModal from "app/components/CancelPurchaseConfirmationModal";

import styles from "./styles.module.scss";

type PendingPurchaseBannerProps = {
  programId?: string;
  purchase?: StripePurchaseModel;
  onCancelled?: () => void;
};

export default function PendingPurchaseBanner(props: PendingPurchaseBannerProps) {
  const { programId, purchase, onCancelled } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const userState = useSelector((state: RootState): UserModel => state.user.data);
  const userType = userState?.kind;

  const [pendingPurchase, setPendingPurchase] = useState<StripePurchaseModel | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    // If purchase prop is provided, use it directly
    if (purchase) {
      setPendingPurchase(purchase);
      return;
    }

    // If no programId and no purchase, skip fetch
    // This means we're in list context waiting for purchase prop
    if (!programId && !purchase) {
      return;
    }

    // Only check for athletes
    if (userType !== "athlete") {
      return;
    }

    schedulingFetchGet('/stripe_purchases/pending')
      .then((result) => {
        if (!result || !Array.isArray(result) || result.length === 0) {
          return;
        }

        let matchingPurchase;

        if (programId) {
          // Filter for specific program (program page context)
          const programIdNum = parseInt(programId, 10);
          const purchases = result.map(data => new StripePurchaseModel(data));
          matchingPurchase = purchases.find(purchase =>
            purchase.programIds?.includes(programIdNum) &&
            purchase.isPending &&
            !purchase.isExpired
          );
        } else {
          // Show first pending purchase (unified view context)
          const purchases = result.map(data => new StripePurchaseModel(data));
          matchingPurchase = purchases.find(purchase =>
            purchase.isPending && !purchase.isExpired
          );
        }

        if (matchingPurchase) {
          setPendingPurchase(matchingPurchase); // Already a model instance
        }
      })
      .catch((error) => {
        // Ignore errors silently - we don't want to show errors for "no pending purchase"
        if (error.status !== 403) {
          console.error('Error fetching pending purchase:', error);
        }
      });
  }, [programId, purchase, userType]);

  const handleResumePurchase = () => {
    if (pendingPurchase) {
      history.push(`/purchase/${pendingPurchase.id}`);
    }
  };

  const handleCancelPurchase = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (cancelling || !pendingPurchase) {
      return;
    }

    setCancelling(true);
    try {
      await schedulingFetchPost(`/stripe_purchases/${pendingPurchase.id}/cancel`, {});
      setPendingPurchase(null); // Hide banner instead of redirecting
      setShowCancelModal(false);
      onCancelled?.(); // Notify parent that registration was cancelled
    } catch (err) {
      console.error('Error cancelling purchase:', err);
      if (err?.status === 422) {
        toast.error(err.error || t("Payment is currently being processed. Please try again shortly."));
        setShowCancelModal(false);
      }
      setCancelling(false);
    }
  };

  const handleModalCancel = () => {
    setShowCancelModal(false);
  };

  const handlePurchaseExpired = () => {
    setPendingPurchase(null);
    onCancelled?.(); // Notify parent that registration expired
  };

  // Don't render anything if no pending purchase
  if (!pendingPurchase) {
    return null;
  }

  return (
    <div className={styles.pendingPurchaseBanner}>
      <div className={styles.bannerContent}>
        <div className={styles.leftCol}>
          <div className={styles.bannerText}>
            <span className="ico ico-clock" style={{ fontSize: '1.5rem' }} />
            <strong>
              {programId
                ? t("You have a pending registration for this program")
                : t("You have a pending purchase")
              }
            </strong>
          </div>

          {pendingPurchase.expiresAt && (
            <CountdownTimer
              expiresAt={pendingPurchase.expiresAt}
              runningLabel="Time remaining"
              expiredLabel="Expired"
              onExpire={handlePurchaseExpired}
            />
          )}
        </div>

        <div className={styles.buttonsContainer}>
          <div title={pendingPurchase?.cancellable === false ? t("Payment is being processed") : undefined}>
            <ButtonVariant
              buttonType="secondary-filled"
              title={t("Cancel")}
              onClickFunc={handleCancelPurchase}
              disabled={cancelling || pendingPurchase?.cancellable === false}
            />
          </div>
          <ButtonVariant
            buttonType="primary"
            title={t("Resume")}
            onClickFunc={handleResumePurchase}
            buttonStyles={styles.resumeButton}
          />
        </div>

      </div>

      <CancelPurchaseConfirmationModal
        isOpen={showCancelModal}
        onConfirm={handleConfirmCancel}
        onCancel={handleModalCancel}
        isLoading={cancelling}
        title={!programId ? t("Cancel Purchase") : undefined}
        message={!programId ? t("Please confirm that you would like to cancel your purchase.") : undefined}
      />
    </div>
  );
}
